/* PDP hero entry overlays — fullscreen gallery swiper + video modal.
   Rendered inline (hidden) by the entry theme blocks; slides stay lazy
   until first open. No dependencies: the gallery is a transform track
   with modulo looping, pointer gestures, pixel-dot indicators; the video
   modal attaches HLS on first open via theme.js's exposed attachHls. */
(() => {
  if (customElements.get('pdp-gallery')) return;

  /* body scroll lock that nests correctly when several overlays stack */
  const lock = {
    depth: 0,
    on() {
      if (++this.depth === 1) document.body.style.overflow = 'hidden';
    },
    off() {
      if (this.depth > 0 && --this.depth === 0) document.body.style.overflow = '';
    },
  };

  const FADE_MS = 240;

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.dotsEl = this.querySelector('[data-gallery-dots]');
      /* slides (not the .shopify-block wrappers around them) — order matches */
      this.slides = [...this.querySelectorAll('.pdp-gallery__slide')];
      this.index = 0;
      this._opener = null;
      this._hideTimer = 0;
      this.buildDots();
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-gallery-close]')) return this.close();
        if (e.target.closest('[data-gallery-prev]')) return this.go(this.index - 1);
        if (e.target.closest('[data-gallery-next]')) return this.go(this.index + 1);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
        else if (e.key === 'ArrowRight') this.go(this.index + 1);
      });
      this.wireGestures();
      this.go(0, false);
    }

    buildDots() {
      this.slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pdp-gallery__dot';
        dot.setAttribute('aria-label', `第 ${i + 1} 张`);
        dot.addEventListener('click', () => this.go(i));
        this.dotsEl.appendChild(dot);
      });
    }

    /* modulo index → the track loops forever in both directions */
    go(i, animate = true) {
      const n = this.slides.length;
      if (!n) return;
      this.index = ((i % n) + n) % n;
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translateX(${-this.index * 100}%)`;
      if (!animate) requestAnimationFrame(() => { this.track.style.transition = ''; });
      [...this.dotsEl.children].forEach((dot, di) => {
        dot.classList.toggle('is-active', di === this.index);
        if (di === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    open(opener) {
      clearTimeout(this._hideTimer);
      this._opener = opener || this._opener;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      lock.on();
      this.go(this.index, false);
      this.querySelector('[data-gallery-close]')?.focus();
    }

    close() {
      this.classList.remove('is-open');
      lock.off();
      this._opener?.focus?.();
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => { this.hidden = true; }, FADE_MS);
    }

    /* drag/swipe: follow the finger horizontally, snap on release */
    wireGestures() {
      const vp = this.viewport;
      let startX = 0;
      let startY = 0;
      let dx = 0;
      let pid = -1;
      let axis = '';
      const reset = () => {
        pid = -1;
        axis = '';
        this.track.style.transition = '';
        this.go(this.index);
      };
      vp.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button')) return; /* edge zones & dots are clicks */
        pid = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        dx = 0;
        axis = '';
        vp.setPointerCapture(pid);
        this.track.style.transition = 'none';
      });
      vp.addEventListener('pointermove', (e) => {
        if (pid !== e.pointerId) return;
        const mx = e.clientX - startX;
        const my = e.clientY - startY;
        if (!axis) {
          if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
          axis = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
          if (axis === 'y') return reset(); /* vertical drags belong to the page */
        }
        if (axis !== 'x') return;
        dx = mx;
        const base = -this.index * vp.clientWidth;
        this.track.style.transform = `translateX(${base + dx}px)`;
      });
      vp.addEventListener('pointerup', (e) => {
        if (pid !== e.pointerId) return;
        const threshold = Math.min(vp.clientWidth * 0.18, 120);
        const commit = axis === 'x' && Math.abs(dx) > threshold;
        const dir = dx < 0 ? 1 : -1;
        pid = -1;
        axis = '';
        this.track.style.transition = '';
        commit ? this.go(this.index + dir) : this.go(this.index);
      });
      vp.addEventListener('pointercancel', () => {
        if (pid !== -1) reset();
      });
    }
  }

  class PdpVideoModal extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.soundBtn = this.querySelector('[data-vmodal-sound]');
      this._opener = null;
      this._hideTimer = 0;
      this._attached = false;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-vmodal-close]')) return this.close();
        /* sound toggle button, or a tap on the video itself */
        if (e.target.closest('[data-vmodal-sound], .pdp-vmodal__stage')) this.toggleSound();
      });
    }

    open(opener) {
      clearTimeout(this._hideTimer);
      this._opener = opener || this._opener;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      lock.on();
      this.querySelector('[data-vmodal-close]')?.focus();
      if (!this.video) return;
      if (!this._attached) {
        this._attached = true;
        window.PD?.attachHls?.(this.video); /* theme.js: m3u8 → MP4 fallback */
      }
      /* sound on by default — opening happens inside a user gesture (the
         entry click), which unlocks unmuted playback; a stricter browser
         that still blocks it starts muted, then unmutes on first frame —
         unmuting an already-playing video is never policy-gated */
      this.video.muted = false;
      this.video.currentTime = 0;
      this.updateSound();
      this.video.play().catch(() => {
        this.video.muted = true;
        this.updateSound();
        const un = () => {
          this.video.removeEventListener('playing', un);
          if (!this.video.paused) { this.video.muted = false; this.updateSound(); }
        };
        this.video.addEventListener('playing', un);
        this.video.play().catch(() => {});
      });
    }

    close() {
      this.classList.remove('is-open');
      if (this.video) {
        this.video.pause();
      }
      lock.off();
      this._opener?.focus?.();
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => { this.hidden = true; }, FADE_MS);
    }

    toggleSound() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      if (!this.video.muted) this.video.play().catch(() => {});
      this.updateSound();
    }

    updateSound() {
      const on = this.video && !this.video.muted;
      this.soundBtn?.setAttribute('aria-pressed', String(!!on));
      this.soundBtn?.querySelector('.pdp-vmodal__sound-label')?.replaceChildren(on ? 'SOUND ON' : 'SOUND OFF');
    }
  }

  customElements.define('pdp-gallery', PdpGallery);
  customElements.define('pdp-video-modal', PdpVideoModal);

  /* specs sheet: static grouped table — open/close only */
  class PdpSpecsModal extends HTMLElement {
    connectedCallback() {
      this._opener = null;
      this._hideTimer = 0;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-specs-close]')) this.close();
      });
    }
    open(opener) {
      clearTimeout(this._hideTimer);
      this._opener = opener || this._opener;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      lock.on();
      this.querySelector('[data-specs-close]')?.focus();
    }
    close() {
      this.classList.remove('is-open');
      lock.off();
      this._opener?.focus?.();
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => { this.hidden = true; }, FADE_MS);
    }
  }
  customElements.define('pdp-specs-modal', PdpSpecsModal);

  /* entry buttons open the overlay living next to them in the same wrap */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pdp-open]');
    if (!btn) return;
    const overlay = btn.closest('.pdp-entry-wrap')?.querySelector('pdp-gallery, pdp-video-modal, pdp-specs-modal');
    if (!overlay) return;
    e.preventDefault();
    overlay.open(btn);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document
      .querySelectorAll('pdp-gallery:not([hidden]), pdp-video-modal:not([hidden]), pdp-specs-modal:not([hidden])')
      .forEach((el) => el.close());
  });
})();
